import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F7FA]">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center mb-12">
            <img src="/promptyai-logo.png" alt="Promptyai.app" className="h-16 w-auto object-contain" />
          </Link>
          <h1 className="font-display text-4xl font-bold tracking-tight mb-2">Welcome back.</h1>
          <p className="text-[#64748B] mb-8">Sign in to continue building precision prompts.</p>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-[#14B8A6] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#1F3A8A] hover:bg-[#1E40AF] text-white" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <p className="text-sm text-[#64748B] text-center">
              No account? <Link to="/register" className="text-[#14B8A6] font-semibold hover:underline">Create one</Link>
            </p>
          </form>
        </div>
      </div>
      {/* Right: brand panel */}
      <div className="hidden lg:block relative bg-[#1F3A8A] bg-grid-dark overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1F3A8A] via-[#1F3A8A] to-[#14B8A6]/30"></div>
        <div className="relative h-full flex items-end p-12 text-white">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-3">Why Promptyai</div>
            <p className="font-display text-3xl font-bold leading-tight max-w-md">
              "9 fields. 1 token. Every output feels like it was written by a senior prompt engineer."
            </p>
            <p className="mt-4 text-white/60">— Maya R., Head of Content</p>
          </div>
        </div>
      </div>
    </div>
  );
}
